import { Box, Flex, Grid, GridItem, IconButton, Spinner } from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MdClose, MdNavigateBefore, MdNavigateNext, MdOutlineFirstPage, MdOutlineLastPage } from 'react-icons/md';
import moment from 'moment';

import { EquipmentRepairOrder } from '@app/api/models/equipment';
import { useFetchData, useApi } from '@app/hooks';
import { ProfileContext } from '@app/contexts/profile';
import { API_FEAT_DATE_FORMAT } from '@app/utils';

import { Paper } from '../common/paper';
import RepairCard from './repair-card';
import { Txt } from '../common/text';
import { DateRangeField, SwitchField, TextField } from '../common';
import { DateRange } from '../common/date-range-picker/types';

const PAGE_SIZE = 10;

export const EquipmentsRepairsMobileView = () => {
  const [orders, setOrders] = useState<EquipmentRepairOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [period, setPeriod] = useState<DateRange | undefined>({
    startDate: moment().subtract(1, 'month'),
    endDate: moment()
  });
  const [onlyOwned, setOnlyOwned] = useState(true);
  const [serial, setSerial] = useState<string>();
  
  const { profile } = useContext(ProfileContext);
  
  const api = useApi();
  const [fetchData] = useFetchData<EquipmentRepairOrder>({ method: api.equipments.getEquipmentsRepairs });

  const fetch = useCallback( async () => { 
    setLoading(true);
    const { total, data } = await fetchData({
      pagination: { page, pageSize: PAGE_SIZE },
      sort: { name: 'id', direction: 'desc' },
      filter: [],
      filterRaw: { 
        user: onlyOwned ? profile.username : undefined,
        ...(period ? { create_date_range: [period.startDate.format(API_FEAT_DATE_FORMAT), period.endDate.format(API_FEAT_DATE_FORMAT)] } : {}),
        ...(serial ? { serial_num: serial } : {}),
      }
    });
    setTotal(total);
    setOrders(data);
    setLoading(false);
  }, [page, profile, period, serial, onlyOwned]);

  useEffect(() => {
    void fetch();
  }, [page, period, serial, onlyOwned]);


  const lastPage = useMemo(
    () => Math.ceil(total / PAGE_SIZE),
    [total]
  );

  const onPrevPageClick = () => {
    page !== 1 && setPage(page - 1);
  };

  const onNextPageClick = () => {
    page !== lastPage && setPage(page + 1);
  };
  
  return (
    <>
      <Grid templateColumns='1fr'>
        <GridItem>
          <Paper>
            <Flex gap={3} alignItems='center' px={5} py={3} justifyContent='center' flexDirection='column'  >
              <DateRangeField 
                id='period'
                label="Период"
                value={period}
                onChange={setPeriod}
              />
              <TextField
                id="serial"
                label="Серийный номер ХО"
                value={serial}
                onChange={setSerial}
                rightElement={<IconButton 
                  icon={<MdClose />} aria-label="first-page" 
                  backgroundColor="transparent" 
                  borderRadius="50%"
                  size="xs"    
                  onClick={() => setSerial('')}
                />}
              />
              <SwitchField 
                label='Только собственные'
                id='onlyOwned'
                value={onlyOwned}
                onChange={setOnlyOwned}
              />
            </Flex>
          </Paper>
        </GridItem>
        {loading && <GridItem colSpan={{ base: 1, md: 2 }}>
          <Box
            position="absolute"
            width="100%"
            height="300px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner />
          </Box>
        </GridItem>}
        {!loading && orders.length === 0 && <GridItem colSpan={{ base: 1, md: 2 }}>
          <Box minHeight={200} display='flex' gap={4} alignItems='center' justifyContent='center'>
            <Txt textColor='gray.500'>Пусто</Txt>
          </Box>
        </GridItem>}
        {!loading && orders.map((item) => (
          <GridItem key={item.id}>
            <RepairCard item={item} />
          </GridItem>
        ))}

        <GridItem>
          <Paper>
            <Box display='flex' alignItems='center' gap={2} px={5} py={3} justifyContent='center' >
              <IconButton size='sm' icon={<MdOutlineFirstPage size='24px' />} aria-label="first-page" onClick={() => setPage(1)}     />
              <IconButton size='sm' 
                icon={<MdNavigateBefore size='24px' />} aria-label="prev-page" 
                opacity={page === 1 ? 0.5 : 1} onClick={onPrevPageClick} 
              />
              <Txt mx={2}>{page}</Txt>
              <IconButton size='sm' 
                icon={<MdNavigateNext size='24px' />} aria-label="next-page"
                opacity={page === lastPage ? 0.5 : 1} onClick={onNextPageClick} 
              />
              <IconButton size='sm' icon={<MdOutlineLastPage size='24px' />} aria-label="last-page" onClick={() => setPage(lastPage)}/>
            </Box>
          </Paper>
        </GridItem>
      </Grid>
    </>
  );
};